// ===========================================
// Service Modal Component
// ===========================================
// Modal for adding/editing services attached to projects

import React, { useState, useEffect } from 'react';
import { useProjects } from '../context/ProjectContext';
import { X, Loader, Plus, Server, Eye, EyeOff, Save } from 'lucide-react';
import './Modal.scss';

// Service type configuration
const serviceTypes = [
  { value: 'hosting', label: 'Hosting', icon: '🌐' },
  { value: 'database', label: 'Database', icon: '🗄️' },
  { value: 'llm', label: 'LLM / AI', icon: '🤖' },
  { value: 'analytics', label: 'Analytics', icon: '📊' },
  { value: 'auth', label: 'Authentication', icon: '🔐' },
  { value: 'storage', label: 'Storage', icon: '💾' },
  { value: 'cdn', label: 'CDN', icon: '⚡' },
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'monitoring', label: 'Monitoring', icon: '📡' },
  { value: 'other', label: 'Other', icon: '🔧' }
];

const ServiceModal = ({ projectId, service = null, onClose, onSaved }) => {
  const { addService } = useProjects();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCredentials, setShowCredentials] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'hosting',
    tier: '',
    monthlyCost: '',
    loginUrl: '',
    docsUrl: '',
    credentials: {
      apiKey: '',
      connectionString: '',
      username: '',
      password: '',
      notes: ''
    }
  });

  // Initialize form with service data if editing
  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name || '',
        type: service.type || 'hosting',
        tier: service.tier || '',
        monthlyCost: service.monthlyCost?.toString() || '',
        loginUrl: service.loginUrl || '',
        docsUrl: service.docsUrl || '',
        credentials: {
          apiKey: '', // Don't pre-fill sensitive data
          connectionString: '',
          username: '',
          password: '',
          notes: service.credentials?.notes || ''
        }
      });
    }
  }, [service]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('credentials.')) {
      const credField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        credentials: { ...prev.credentials, [credField]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name.trim()) {
      setError('Service name is required');
      return;
    }
    
    if (!formData.type) {
      setError('Service type is required');
      return;
    }
    
    setLoading(true);
    
    // Build service data
    const serviceData = {
      name: formData.name,
      type: formData.type,
      tier: formData.tier || undefined,
      monthlyCost: parseFloat(formData.monthlyCost) || 0,
      loginUrl: formData.loginUrl || undefined,
      docsUrl: formData.docsUrl || undefined
    };
    
    // Include credentials if any are provided
    const hasCredentials = Object.values(formData.credentials).some(v => v);
    if (hasCredentials) {
      serviceData.credentials = {};
      if (formData.credentials.apiKey) {
        serviceData.credentials.apiKey = formData.credentials.apiKey;
      }
      if (formData.credentials.connectionString) {
        serviceData.credentials.connectionString = formData.credentials.connectionString;
      }
      if (formData.credentials.username) {
        serviceData.credentials.username = formData.credentials.username;
      }
      if (formData.credentials.password) {
        serviceData.credentials.password = formData.credentials.password;
      }
      if (formData.credentials.notes) {
        serviceData.credentials.notes = formData.credentials.notes;
      }
    }
    
    const result = await addService(projectId, serviceData);
    
    if (result.success) {
      onSaved(result.data);
      onClose();
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const isEditing = !!service;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--large" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal__header">
          <h2>
            <Server size={20} />
            {isEditing ? 'Edit Service' : 'Add Service'}
          </h2>
          <button className="modal__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form className="modal__form" onSubmit={handleSubmit}>
          {error && (
            <div className="modal__error">{error}</div>
          )}

          {/* Name & Type */}
          <div className="modal__row">
            <div className="modal__field">
              <label htmlFor="name">Service Name *</label>
              <input
                id="name"
                name="name"
                type="text"
                placeholder="Vercel, MongoDB Atlas, OpenAI..."
                value={formData.name}
                onChange={handleChange}
                disabled={loading}
                autoFocus
              />
            </div>
            
            <div className="modal__field">
              <label htmlFor="type">Type *</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                disabled={loading}
              >
                {serviceTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tier & Cost */}
          <div className="modal__row">
            <div className="modal__field">
              <label htmlFor="tier">Plan / Tier</label>
              <input
                id="tier"
                name="tier"
                type="text"
                placeholder="Free, Pro, Enterprise..."
                value={formData.tier}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
            
            <div className="modal__field">
              <label htmlFor="monthlyCost">Monthly Cost ($)</label>
              <input
                id="monthlyCost"
                name="monthlyCost"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={formData.monthlyCost}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </div>

          {/* URLs */}
          <div className="modal__row">
            <div className="modal__field">
              <label htmlFor="loginUrl">Login / Dashboard URL</label>
              <input
                id="loginUrl"
                name="loginUrl"
                type="url"
                placeholder="https://dashboard.service.com"
                value={formData.loginUrl}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
            
            <div className="modal__field">
              <label htmlFor="docsUrl">Documentation URL</label>
              <input
                id="docsUrl"
                name="docsUrl"
                type="url"
                placeholder="https://docs.service.com"
                value={formData.docsUrl}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </div>

          {/* Credentials Section */}
          <div className="modal__section">
            <button 
              type="button"
              className="modal__section-toggle"
              onClick={() => setShowCredentials(!showCredentials)}
            >
              {showCredentials ? <EyeOff size={16} /> : <Eye size={16} />}
              {showCredentials ? 'Hide Credentials' : 'Add Credentials (Encrypted)'}
            </button>
            
            {showCredentials && (
              <div className="modal__section-content">
                <div className="modal__field">
                  <label htmlFor="credentials.apiKey">API Key</label>
                  <input
                    id="credentials.apiKey"
                    name="credentials.apiKey"
                    type="password"
                    placeholder="••••••••••••••••"
                    value={formData.credentials.apiKey}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
                
                <div className="modal__field">
                  <label htmlFor="credentials.connectionString">Connection String</label>
                  <input
                    id="credentials.connectionString"
                    name="credentials.connectionString"
                    type="password"
                    placeholder="mongodb+srv://..."
                    value={formData.credentials.connectionString}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
                
                <div className="modal__row">
                  <div className="modal__field">
                    <label htmlFor="credentials.username">Username</label>
                    <input
                      id="credentials.username"
                      name="credentials.username"
                      type="text"
                      placeholder="Username"
                      value={formData.credentials.username}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="modal__field">
                    <label htmlFor="credentials.password">Password</label>
                    <input
                      id="credentials.password"
                      name="credentials.password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.credentials.password}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                </div>
                
                <div className="modal__field">
                  <label htmlFor="credentials.notes">Credential Notes</label>
                  <textarea
                    id="credentials.notes"
                    name="credentials.notes"
                    placeholder="Additional info about these credentials..."
                    value={formData.credentials.notes}
                    onChange={handleChange}
                    disabled={loading}
                    rows={2}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="modal__actions">
            <button 
              type="button" 
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  {isEditing ? 'Saving...' : 'Adding...'}
                </>
              ) : (
                <>
                  {isEditing ? <Save size={16} /> : <Plus size={16} />}
                  {isEditing ? 'Save Service' : 'Add Service'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceModal;

